import type { ReactPayPalScriptOptions } from '@paypal/react-paypal-js/dist/types/types/scriptProviderTypes'
import { PaymentStore } from 'shop/stores/payment.store'
import { getDynamicCheckoutCtaText } from 'constants/paymentSetting.constants'
import { useNextRouter } from 'shop/hooks/use-next-js-router'
import { useShopStores } from 'shop/hooks/use-store'
import themeStore from 'shop/stores/theme.store'
import { isProduction } from 'utils/env.utils'
import { PaymentSessionResponse } from 'shop/api/orders-v2.api'
import { LocalStorageService } from 'utils/local-storage.utils'
import { PayPalBuyButtons } from './PayPalControls'

interface PayPalExpressButtonProps {
  paymentStore: PaymentStore
  createPaymentSession: () => Promise<PaymentSessionResponse>
}

const buyBtnText = {
  buy_now: I18n.t('react.shared.button.buy_now'),
  agree_on_terms_and_pay: I18n.t('react.shared.button.agree_on_terms_and_pay'),
  complete_order: I18n.t('react.shared.button.complete_order'),
  accept_and_pay_now: I18n.t('react.shared.button.accept_and_pay_now'),
}

interface SearchParams {
  is_preview: string
  cabinet_preview: string
}

export const PayPalExpressButton = ({ paymentStore, createPaymentSession }: PayPalExpressButtonProps) => {
  const { sellerStore } = useShopStores()
  const {
    params: { is_preview: isPreview, cabinet_preview: isCabinetPreview },
  } = useNextRouter<SearchParams>()

  const payerCountry = paymentStore.payerForms?.userData?.payer?.country?.code

  const paypalScriptOptions: Partial<ReactPayPalScriptOptions> = {
    ...(!isProduction() && (!!isCabinetPreview || !!isPreview || LocalStorageService.getItem('aqa_paypal_country'))
      ? { buyerCountry: payerCountry }
      : {}),
    currency: paymentStore.currency.toUpperCase(),
  }

  const isDynamicButton = themeStore.ppTemplate?.theme?.paymentBuyBtnText === 'dynamic_cta'

  const resellerBuyButton = sellerStore.item.powerSeller ? buyBtnText['accept_and_pay_now'] : buyBtnText['buy_now']

  const dynamicTextButton = isDynamicButton
    ? getDynamicCheckoutCtaText()[paymentStore.store?.paymethods?.form] ||
      I18n.t('react.shared.button.accept_and_pay_now')
    : buyBtnText[themeStore.ppTemplate?.theme?.paymentBuyBtnText] || resellerBuyButton

  const btnStyle = themeStore.getBuyBtnStyle(0)
  !btnStyle.backgroundColor && delete btnStyle.backgroundColor

  const transformPaymentSessionToOrderResponse = (paymentSession: PaymentSessionResponse) => ({
    status: 201,
    statusText: 'Created',
    success: true,
    data: paymentSession.data,
    redirectLink: paymentSession.data.approvalUrl,
    clientSecret: null,
    token: paymentSession.data.signed_id,
    paypalPaylaterAttributes: {
      orderId: paymentSession.data.transactionReference,
      returnUrl: paymentSession.data.returnUrl,
      cancelUrl: paymentSession.data.cancelUrl,
    },
    setupIntentSecret: null,
  })

  return (
    <PayPalBuyButtons
      paymentStore={paymentStore}
      paypalScriptOptions={paypalScriptOptions}
      buyButtonDisabled={false}
      vaultButton={false}
      submit={async () => {
        const paymentSessionResponse = await createPaymentSession()
        return transformPaymentSessionToOrderResponse(paymentSessionResponse)
      }}
      style={{ disableMaxWidth: true, height: 44 }}
      isExpress={true}
      showPayLater={false}
    >
      <button style={btnStyle}>
        {paymentStore.invoice?.token ? I18n.t('react.shared.button.pay_now') : dynamicTextButton}
      </button>
    </PayPalBuyButtons>
  )
}
